import { useQuery } from '@tanstack/react-query';

import { getPacing } from '../api/ach/contracts';
import { SPEND_DEFERRED } from '../constants/ach/apiUrls';
import { useConfig } from './useConfig';

// ACH-bj8d: second-wave fetch of the BigQuery spend payload the contracts index no
// longer stitches inline (?spend=deferred). Same shape as useContractsKpiPerformance:
// keyed by the sorted id set, never blocks or errors the page. Returns the raw
// {pacing: {"<id>": {...}}} envelope.
function useContractsPacing(ids: number[]) {
  const { achApiHost } = useConfig();
  const sortedIds = [...ids].sort((a, b) => a - b);

  return useQuery({
    queryKey: ['contractsPacing', achApiHost, sortedIds],
    // Request the sorted set too so the fetched ids always match the cache key
    // (the response is a map keyed by contract id, so order is irrelevant).
    queryFn: async () => {
      try {
        return await getPacing(achApiHost, sortedIds);
      } catch (err) {
        // Pacing is supplementary — on any failure degrade to no pacing data (the
        // Pacing / 5-Day columns render empty) rather than erroring or logging out.
        if (import.meta.env.DEV) {
          // eslint-disable-next-line no-console
          console.warn('useContractsPacing: ACH fetch failed; hiding pacing columns', err);
        }
        return { pacing: {} };
      }
    },
    retry: false,
    // Without the deferral the index rows already carry the spend payload inline, so
    // there is nothing to refetch — this keeps SPEND_DEFERRED=undefined a true rollback.
    enabled: ids.length > 0 && Boolean(SPEND_DEFERRED),
  });
}

export default useContractsPacing;
